<!-- src/lib/components/CanvasView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import { cardHeat, heatBorderColor, heatGlow } from '../stores/vitals.svelte.ts';
  import type { Card, CanvasPosition } from '../types.ts';

  interface Props {
    boardId: string;
    onOpenCard: (id: string) => void;
  }
  let { boardId, onOpenCard }: Props = $props();

  let positions = $state<Record<string, { x: number; y: number }>>({});
  let loaded    = $state(false);

  let dragging = $state<{ card_id: string; startX: number; startY: number; originX: number; originY: number } | null>(null);

  const allCards = $derived(
    pmStore.boardDetails[boardId]?.columns.flatMap(col => col.cards) ?? [] as Card[]
  );

  const filamentPairs = $derived((() => {
    const byColumn: Record<string, Card[]> = {};
    for (const card of allCards) {
      if (!card.column_id) continue;
      (byColumn[card.column_id] ??= []).push(card);
    }
    const pairs: Array<{ a: Card; b: Card }> = [];
    for (const group of Object.values(byColumn)) {
      for (let i = 0; i < group.length - 1; i++) {
        pairs.push({ a: group[i], b: group[i + 1] });
      }
    }
    return pairs;
  })());

  $effect(() => {
    if (!boardId) return;
    loaded = false;
    api<CanvasPosition[]>(`/api/v1/boards/${boardId}/canvas`).then(data => {
      const map: Record<string, { x: number; y: number }> = {};
      for (const p of data) {
        map[p.card_id] = { x: p.x, y: p.y };
      }
      const cards = pmStore.boardDetails[boardId]?.columns.flatMap(c => c.cards) ?? [];
      cards.forEach((card, i) => {
        if (!map[card.id]) {
          const col = i % 5;
          const row = Math.floor(i / 5);
          map[card.id] = { x: 40 + col * 280, y: 40 + row * 200 };
        }
      });
      positions = map;
      loaded = true;
    }).catch(err => { console.warn('canvas load failed:', err); loaded = true; });
  });

  function cardPos(id: string): { x: number; y: number } {
    return positions[id] ?? { x: 40, y: 40 };
  }

  function onPointerDown(e: PointerEvent, cardId: string) {
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const pos = cardPos(cardId);
    dragging = { card_id: cardId, startX: e.clientX, startY: e.clientY, originX: pos.x, originY: pos.y };
  }

  function onPointerMove(e: PointerEvent, cardId: string) {
    if (!dragging || dragging.card_id !== cardId) return;
    const dx = e.clientX - dragging.startX;
    const dy = e.clientY - dragging.startY;
    positions[cardId] = { x: dragging.originX + dx, y: dragging.originY + dy };
  }

  function onPointerUp(e: PointerEvent, cardId: string) {
    if (!dragging || dragging.card_id !== cardId) return;
    const pos = cardPos(cardId);
    dragging = null;
    api(`/api/v1/boards/${boardId}/canvas`, {
      method: 'PUT',
      body: JSON.stringify({ positions: [{ card_id: cardId, x: pos.x, y: pos.y }] }),
    }).catch(err => console.warn('canvas save failed:', err));
  }

  const PRIORITY_COLOR: Record<string, string> = {
    urgent: '#ff4444',
    high:   'oklch(0.78 0.16 85)',
    medium: '#ffcc00',
    low:    'oklch(0.58 0.22 265)',
    none:   '#444',
  };
</script>

<div class="canvas-wrap">
  {#if !loaded}
    <div class="canvas-empty">Loading canvas…</div>
  {:else if allCards.length === 0}
    <div class="canvas-empty">No cards — add some from the Board view.</div>
  {:else}
    <div class="canvas-surface">
      <svg class="filament-layer" aria-hidden="true">
        {#each filamentPairs as pair (pair.a.id + '_' + pair.b.id)}
          {@const posA = positions[pair.a.id]}
          {@const posB = positions[pair.b.id]}
          {#if posA && posB}
            {@const x1 = posA.x + 120}
            {@const y1 = posA.y + 45}
            {@const x2 = posB.x + 120}
            {@const y2 = posB.y + 45}
            {@const midX = (x1 + x2) / 2}
            {@const midY = (y1 + y2) / 2}
            {@const dx = x2 - x1}
            {@const dy = y2 - y1}
            {@const len = Math.sqrt(dx * dx + dy * dy) || 1}
            {@const px = -dy / len}
            {@const py = dx / len}
            {@const cp1x = midX + px * 80}
            {@const cp1y = midY + py * 80}
            {@const isDragging = dragging?.card_id === pair.a.id || dragging?.card_id === pair.b.id}
            <path
              d="M {x1},{y1} Q {cp1x},{cp1y} {x2},{y2}"
              stroke={isDragging ? 'oklch(0.72 0.28 310 / 0.12)' : 'oklch(0.74 0.28 310 / 0.10)'}
              stroke-width="1"
              fill="none"
            />
          {/if}
        {/each}
      </svg>
      {#each allCards as card (card.id)}
        {@const pos = cardPos(card.id)}
        {@const heat = cardHeat(card.updated_at)}
        <div
          class="card-tile"
          class:dragging-tile={dragging?.card_id === card.id}
          style="transform: translate({pos.x}px, {pos.y}px); border-color: {heatBorderColor(heat)}; {heat > 0.05 ? `box-shadow: ${heatGlow(heat)};` : ''}"
          role="presentation"
          onpointerdown={(e) => onPointerDown(e, card.id)}
          onpointermove={(e) => onPointerMove(e, card.id)}
          onpointerup={(e) => onPointerUp(e, card.id)}
          onpointercancel={() => { dragging = null; }}
        >
          <div class="tile-header">
            <span
              class="prio-dot"
              style="background: {PRIORITY_COLOR[card.priority] ?? '#444'}"
              title={card.priority}
            ></span>
            <span class="tile-title"
              role="button"
              tabindex="0"
              onclick={(e) => { e.stopPropagation(); onOpenCard(card.id); }}
              onkeydown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onOpenCard(card.id); } }}>
              {card.title}
            </span>
          </div>
          {#if card.due_date}
            <div class="tile-due">{card.due_date}</div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .canvas-wrap {
    flex: 1;
    overflow: hidden;
    position: relative;
    background: #050608;
    background-image: radial-gradient(circle, oklch(0.74 0.28 310 / 0.10) 1px, transparent 1px);
    background-size: 32px 32px;
  }
  .canvas-empty {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #444;
    font-size: 0.875rem;
    font-family: system-ui, sans-serif;
  }
  .canvas-surface {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .filament-layer {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: visible;
  }
  .card-tile {
    position: absolute;
    top: 0;
    left: 0;
    width: 240px;
    background: #0d1117;
    border: 1px solid oklch(0.74 0.28 310 / 0.16);
    border-radius: 8px;
    padding: 0.75rem 1rem;
    cursor: grab;
    user-select: none;
    pointer-events: all;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
    transition: border-color 100ms ease;
  }
  .card-tile:hover {
    border-color: oklch(0.74 0.28 310 / 0.32);
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.7), 0 0 8px oklch(0.74 0.28 310 / 0.10);
  }
  .dragging-tile {
    cursor: grabbing;
    border-color: oklch(0.74 0.28 310 / 0.49);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8), 0 0 16px oklch(0.74 0.28 310 / 0.16);
    z-index: 10;
    will-change: transform;
  }
  .tile-header {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .prio-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 0.3rem;
  }
  .tile-title {
    color: #c8ccd8;
    font-size: 0.8125rem;
    line-height: 1.45;
    font-family: system-ui, sans-serif;
    cursor: pointer;
    word-break: break-word;
  }
  .tile-title:hover {
    color: oklch(0.80 0.26 310);
  }
  .tile-due {
    margin-top: 0.5rem;
    font-size: 0.7rem;
    color: #666;
    font-family: system-ui, sans-serif;
    padding-left: 1.25rem;
  }
</style>
